import React, { useEffect, useState } from 'react';
import { LoadingSpinner } from '@paypalcorp/pp-react-loading-spinner';
import MessagingConfigurator from '@MerchantConfigurator/components/messaging';
import ErrorView from '@MerchantConfigurator/components/messaging/ErrorView';
import {
  DefaultLocale,
  ErrorContents,
  InputRegexPatterns,
  MessagingPlacements,
  SupportedLocales
} from '@MerchantConfigurator/constants';
import { ApplicationContext } from '@MerchantConfigurator/context';
import { getMerchantConfiguratorContent } from '@MerchantConfigurator/utils/content';
import { filterArrayByMatchingValue } from '@MerchantConfigurator/utils/filterUtils';
import {
  getDefaultConfigs,
  shouldShowPartnerExperience
} from '@MerchantConfigurator/utils/messaging';
import { sortPlacements } from '@MerchantConfigurator/utils/sortingUtils';
import {
  validateInputParameter,
  validateMerchantIdentifier,
  validatePlacements
} from '@MerchantConfigurator/utils/validations';
import styles from './MerchantConfigurator.module.less';
import { useParams } from 'react-router';
import { merchantConfiguratorDataSelector } from '../../store/App/selectors';
import { useSelector } from 'react-redux';

const App: React.FC<MerchantConfiguratorProps> = ({
  bnCode = 'CreditMerchantTesting',
  config = {},
  locale = DefaultLocale,
  merchantIdentifier = 'B_AOPSrhQoJG2vLc77uJ-G3w81yeiKA9UcOKDB0e_-lKq9fWAzbKufuLgc7dDWsNt_T1PyQjAr4Tzv3v4M',
  merchantClientId = '',
  partnerClientId = 'B_AcMyHmcKiz0czTHMocagesDQqk9XMXCCBrJaG4d3maQMG7qm0zE2jTWtUMg8lKKCGRRWbjyj0wiwWLtM',
  partnerName = 'Mock Partner',
  placements = ['homepage', 'cart', 'checkout', 'product', 'category'],
  onSave = () => {},
  onUpdate = () => {},
  styleOverrides = {}
}) => {
  merchantIdentifier = merchantIdentifier || merchantClientId;
  const { Provider } = ApplicationContext;
  const { country } = useParams();

  const merchantConfiguratorContent = useSelector(merchantConfiguratorDataSelector);

  const [messagingContent, setMessagingContent] = useState<object | undefined>(
    merchantConfiguratorContent
  );

  // TODO: Do we need to validate the hardcoded data?
  const isBnCodeValid = validateInputParameter(bnCode, InputRegexPatterns.bnCode);
  const isPartnerNameValid = validateInputParameter(partnerName, InputRegexPatterns.partnerName);
  const isPartnerClientIdValid = validateInputParameter(
    partnerClientId,
    InputRegexPatterns.clientId
  );

  const isMerchantIdentifierValid = validateMerchantIdentifier(merchantIdentifier);

  const arePlacementsValid = validatePlacements(placements);

  let standardizedLocale = locale.replace('_', '-');

  if (!SupportedLocales.has(standardizedLocale)) {
    standardizedLocale = DefaultLocale;
  }

  useEffect(() => {
    const getMessagingContent = async () => {
      const result = await getMerchantConfiguratorContent(country);
      setMessagingContent(result);
    };
    if (!messagingContent) {
      getMessagingContent();
    }
  }, []);

  //This is to ensure we order the placements as per design regardles of how they are ordered in the placements input.
  const orderedPlacements = sortPlacements(placements);
  const previewEnbaledPlacements = filterArrayByMatchingValue({
    array: orderedPlacements,
    value: MessagingPlacements.custom_placement
  });
  const showPartnerExperience = shouldShowPartnerExperience({ partnerName });
  const defaultConfigsForSupportedPlacements = getDefaultConfigs({
    placements: orderedPlacements,
    showPartnerExperience
  });
  return (
    <Provider
      value={{
        bnCode,
        config,
        defaultConfigsForSupportedPlacements,
        onSave,
        onUpdate,
        partnerName,
        placements: orderedPlacements,
        previewEnbaledPlacements,
        styleOverrides,
        showPartnerExperience,
        ...messagingContent
      }}
    >
      <div className={styles.merchant_configurator_container}>
        {(() => {
          if (!isMerchantIdentifierValid) {
            return (
              <ErrorView
                description={ErrorContents[standardizedLocale].invalidMerchantIdentifier}
                locale={standardizedLocale}
              />
            );
          }
          if (!isPartnerClientIdValid) {
            return (
              <ErrorView
                description={ErrorContents[standardizedLocale].invalidPartnerClientId}
                locale={standardizedLocale}
              />
            );
          }
          if (!partnerName || !isPartnerNameValid) {
            return (
              <ErrorView
                description={ErrorContents[standardizedLocale].invalidPartnerName}
                locale={standardizedLocale}
              />
            );
          }
          if (!bnCode || !isBnCodeValid) {
            return (
              <ErrorView
                description={ErrorContents[standardizedLocale].invalidBNCode}
                locale={standardizedLocale}
              />
            );
          }
          if (!arePlacementsValid || !previewEnbaledPlacements.length) {
            return (
              <ErrorView
                description={ErrorContents[standardizedLocale].invalidPlacements}
                locale={standardizedLocale}
              />
            );
          }
          if (!messagingContent) {
            return (
              <div data-testid="configurator-loading" className={styles.loading_container}>
                <LoadingSpinner screenReaderText="loading" size="lg" inverse />
              </div>
            );
          }

          return <MessagingConfigurator />;
        })()}
      </div>
    </Provider>
  );
};

export default App;

interface MerchantConfiguratorProps {
  bnCode: string;
  config: object;
  locale: string;
  merchantIdentifier: string;
  merchantClientId: string;
  onSave: () => void;
  onUpdate: () => void;
  partnerClientId: string;
  partnerName: string;
  placements: string[];
  styleOverrides: {
    alignStart: string;
    button: string;
    controlPanelHeader: string;
    controlPanelSubheader: string;
    font: string;
    header: string;
    subheader: string;
    toggle: string;
  };
  ecmContent: object;
}
